/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { NavigationType, useNavigationType, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import EmailStep from './components/EmailStep';
import ConfirmStep from './components/ConfirmStep';
import PasswordStep from './components/PasswordStep';
import AuthBrandPanel from 'components/common/AuthBrandPanel';
import Footer from 'components/common/Footer';
import Loader from 'components/common/Loader';
import { EMAIL_REGISTRATION_STEPS } from 'components/common/constants';
import { INITIAL_PATH_AFTER_LOGIN } from 'routes/constants';
import { setIsSuccessLoginStatus } from 'services/authUtils';
import { trackTelemetryEvent, TELEMETRY_EVENTS } from 'services/telemetry';
import emailRegistrationStore from 'stores/emailRegistrationStore';
import './Registration.scss';

// TRN-7473: whether this document has already rendered the registration page — see the mount effect below.
let hasRenderedInThisDocument = false;

// TRN-6922 — new V2 email-registration flow (Email -> Confirm -> Password), replacing
// components/Registration/User at ROUTE_PATHS.REGISTRATION (old code stays until the whole flow is verified
// working, then gets deleted — see docs/specs/TRN-6922_email_registration/spec.md §6).
//
// Mirrors components/PasswordRecovery/index.js (store-driven multi-step switch) and
// components/Authorization/index.js (TRN-6921 — a new page that bypasses AuthorizationLayout and owns its own
// post-success redirect).
const EmailRegistration = ({ isMagicLink }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  // The emailed magic link is `/password-setup?responseId=..&token=..` (backend UserRegistrationUtils
  // .getVerifyTokenUrl). Captured once: the query is stripped right after reading (effect below), so the
  // one-time token doesn't stay in the address bar / browser history or reach GTM's page_view.
  const [link] = useState(() => ({
    token: isMagicLink ? searchParams.get('token') : null,
    responseId: isMagicLink ? searchParams.get('responseId') : null
  }));
  // Starts true whenever there's a token so the very first render never flashes EmailStep (the renderStep()
  // default) before the effect below even runs. Cleared once verifyToken() settles (success or failure) — at
  // which point emailRegistrationStore.step has already moved to PASSWORD or reset to EMAIL, so renderStep()
  // shows the right thing on its own with no extra flag needed after that.
  const [isVerifyingLink, setIsVerifyingLink] = useState(!!link.token);
  // TRN-7489: no step renders until the mount effect below has reset the store — EmailStep takes its initial
  // e-mail from the store (pre-filled after "Change email"), so rendering it before the reset would show the
  // address an earlier visit left behind instead of starting over.
  const [isStoreReady, setIsStoreReady] = useState(isMagicLink);
  // Survives StrictMode's dev-only effect double-invoke: /token/verify is single-use, and REGISTRATION_START
  // must be reported once per page visit (same as the old components/Registration/User page).
  const isMountHandledRef = useRef(false);

  useEffect(() => {
    if (isMountHandledRef.current) return;
    isMountHandledRef.current = true;

    if (!isMagicLink) {
      // TRN-7473: the Login page now links here in-app instead of with a full page reload, so this module-level
      // store still holds whatever an earlier visit left in it (e.g. the Confirm step of an abandoned attempt).
      // Start over like a fresh page load did — except on browser Back/Forward (POP) to this page already
      // rendered in this document, which returns the user to the step they left, as the browser's back/forward
      // cache did before (and, like it, doesn't report REGISTRATION_START again). A direct page load is a POP
      // too, hence the module-level flag.
      const isReturnToRenderedPage = navigationType === NavigationType.Pop && hasRenderedInThisDocument;
      hasRenderedInThisDocument = true;

      if (!isReturnToRenderedPage) {
        emailRegistrationStore.resetStore();
        trackTelemetryEvent(TELEMETRY_EVENTS.REGISTRATION_START);
      }
      setIsStoreReady(true);
      return;
    }

    setSearchParams({}, { replace: true });
    if (link.token) {
      // The link carries responseId, so this also works in a new tab/device where the store is empty.
      if (link.responseId) {
        emailRegistrationStore.setResponseId(link.responseId);
      }
      emailRegistrationStore.verifyToken(link.token).finally(() => setIsVerifyingLink(false));
    }
  }, []);

  useEffect(() => {
    // Ported from components/Authorization/index.js (TRN-6921) — that same pattern, applied here once
    // /complete succeeds: save tokens (Stage 4), then redirect like any other successful login.
    if (emailRegistrationStore.isRegistrationComplete) {
      setIsSuccessLoginStatus(true);
      // Redirect only after the telemetry request settles (it never throws) — window.location.replace() would
      // otherwise cancel it in flight.
      trackTelemetryEvent(TELEMETRY_EVENTS.REGISTRATION_USER_SUCCESS).finally(() =>
        window.location.replace(INITIAL_PATH_AFTER_LOGIN)
      );
    }
  }, [emailRegistrationStore.isRegistrationComplete]);

  const renderStep = () => {
    if (isVerifyingLink) {
      return <Loader className={'application-loader'} />;
    }

    if (!isStoreReady) {
      return null;
    }

    switch (emailRegistrationStore.step) {
      case EMAIL_REGISTRATION_STEPS.CONFIRM:
        return <ConfirmStep />;
      case EMAIL_REGISTRATION_STEPS.PASSWORD:
        return <PasswordStep />;
      case EMAIL_REGISTRATION_STEPS.EMAIL:
      default:
        return <EmailStep />;
    }
  };

  return (
    <div className={'registration-page'}>
      <AuthBrandPanel />
      <div className={'registration-page__content'}>
        {renderStep()}
        <div className={'registration-page__footer'}>
          <Footer />
        </div>
      </div>
    </div>
  );
};

EmailRegistration.propTypes = {
  isMagicLink: PropTypes.bool
};

export default observer(EmailRegistration);
